import { useFacets, useProductFilters, useNostoAppState } from "@nosto/search-js/preact/hooks"
import TermsFacet from "./TermsFacet/TermsFacet"
import RangeFacet from "./RangeFacet/RangeFacet"
import Icon from "@/elements/Icon/Icon"
import { SearchStatsFacet, SearchTermsFacet } from "@nosto/nosto-js/client"
import styles from "./FilterSidebar.module.css"
import Button from "@/elements/Button/Button"
import { cl } from "@nosto/search-js/utils"
import { useFilterSidebar } from "@/contexts/SidebarContext"
import SpecialFilters, { SPECIAL_FILTER_FIELDS } from "./SpecialFilters/SpecialFilters"
import SelectedFilters from "../SelectedFilters/SelectedFilters"
import Heading from "@/elements/Heading/Heading"
import { useCallback, useEffect, useRef } from "preact/hooks"

type ToggleProps = {
  className?: string
  onClick?: () => void
}

function ToggleSidebarButton({ className, onClick }: ToggleProps = {}) {
  return (
    <Button className={cl(styles.close, className)} onClick={onClick}>
      <Icon name="close" />
    </Button>
  )
}

export default function FilterSidebar() {
  const { facets } = useFacets()
  const { filters, removeAll } = useProductFilters()
  const total = useNostoAppState(state => state.response?.products?.total ?? 0)
  const { isOpen, setOpen } = useFilterSidebar()
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen && !dialog.open) {
      dialog.showModal()
    } else if (!isOpen && dialog.open) {
      dialog.close()
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === event.currentTarget) {
        setOpen(false)
      }
    },
    [setOpen]
  )

  if (facets?.length === 0) {
    return null
  }

  // The special filters (e.g. availability) are surfaced as toggles above the
  // menu, so keep them out of the regular facet list.
  const menuFacets = facets?.filter(facet => !SPECIAL_FILTER_FIELDS.includes(facet.field))

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog ref={dialogRef} className={styles.dialog} onClose={handleClose} onClick={handleBackdropClick}>
      <div className={styles.content}>
        {/* Desktop only: special filters sit above the FILTERS band. */}
        <div className={styles.desktopSpecial}>
          <SpecialFilters />
        </div>

        <div className={styles.header}>
          <Heading>Filters</Heading>
          <ToggleSidebarButton onClick={() => setOpen(false)} />
        </div>

        {/* Mobile only: active-filter chips live inside the drawer. */}
        {filters.length > 0 && (
          <div className={styles.mobileActive}>
            <SelectedFilters />
          </div>
        )}

        <div className={styles.menuArea}>
          {/* Mobile only: special filters lead the list alongside the facets. */}
          <div className={styles.mobileSpecial}>
            <SpecialFilters />
          </div>
          <ul className={styles.facets}>
            {menuFacets?.map(facet => {
              switch (facet.type) {
                case "terms":
                  return <TermsFacet key={facet.id} facet={facet as SearchTermsFacet} />
                case "stats":
                  return <RangeFacet key={facet.id} facet={facet as SearchStatsFacet} />
                default:
                  return null
              }
            })}
          </ul>
        </div>

        {/* Mobile only: sticky footer actions. */}
        <div className={styles.mobileFooter}>
          <button type="button" className={styles.footerClear} onClick={removeAll}>
            Clear All
          </button>
          <button type="button" className={styles.footerApply} onClick={() => setOpen(false)}>
            Apply ({total})
          </button>
        </div>
      </div>
    </dialog>
  )
}
